// Color palette names follow design system tokens.
// Off-palette colors (pending design decision) are grouped at the bottom — see GH issue #23244.
export const colorPalette = {
  // ---- Light ----
  white: '#ffffff',
  white_op_10: 'rgba(255, 255, 255, 0.10)',
  white_op_15: 'rgba(255, 255, 255, 0.15)',
  white_op_30: 'rgba(255, 255, 255, 0.3)',
  beige_25: '#f7f3ef',
  beige_25_op_30: 'rgba(247, 243, 239, 0.3)',
  beige_40: '#f1ece4',
  beige_50: '#eae2d6',
  beige_50_op_13: 'rgba(234, 226, 214, 0.13)',
  beige_50_op_50: 'rgba(234, 226, 214, 0.5)',
  beige_50_op_60: 'rgba(234, 226, 214, 0.60)',
  beige_50_op_70: 'rgba(234, 226, 214, 0.7)',
  beige_50_op_75: 'rgba(234, 226, 214, 0.75)',
  beige_60: '#ddd1c0',
  beige_75: '#b4a794',
  beige_90: '#8f8779',

  // ---- Dark (Green) ----
  green_50: '#afbe2d',
  green_75: '#717832',
  green_75_op_10: 'rgba(113, 120, 50, 0.10)',
  green_75_op_30: 'rgba(113, 120, 50, 0.3)',
  green_75_op_40: 'rgba(113, 120, 50, 0.4)',
  green_75_op_60: 'rgba(113, 120, 50, 0.60)',
  green_75_op_75: 'rgba(113, 120, 50, 0.75)',
  green_75_10: '#f1f1ea',
  green_80: '#52583c',
  green_90: '#343b1b',
  green_90_op_80: 'rgba(52, 59, 27, 0.8)',
  green_100: '#1b2514',

  // ---- Accent (Orange) ----
  orange_50: '#f89521',
  orange_50_op_08: 'rgba(248, 149, 33, 0.08)',
  orange_50_op_10: 'rgba(248, 149, 33, 0.1)',
  orange_50_op_50: 'rgba(248, 149, 33, 0.5)',
  orange_50_op_70: 'rgba(248, 149, 33, 0.7)',
  orange_75: '#da7228',

  // ---- Error (Red) ----
  red_50: '#ff5b3b',
  red_50_op_10: 'rgba(255, 91, 59, 0.1)',
  red_100: '#9e2921',
  red_100_10: '#f5eae9',

  // ---- Off-palette (pending design decision; see #23244) ----
  green_legacy_86c440: '#86c440',
  grey_10_op_38: 'rgba(52, 59, 27, 0.38)',
  orange_40: 'rgba(248, 149, 33, 0.4)'
};

export const colors = {
  green: '#32ddc2',
  green_light: '#76fff5',
  green_80: colorPalette.green_80,
  green_dark: '#00aa92',
  green_dark_90: 'rgba(27, 37, 20, 0.9)',
  red: '#d30000',
  red_light: '#ff5030',
  red_dark: '#990000',
  red_dark_90: 'rgba(153, 0, 0, 0.9)',
  black: '#000',
  gray: '#f2f7fa',
  white: '#fff',
  gray_01: '#eeeeee',
  gray_02: '#f2f5f6',
  gray_03: '#5b5b5b',
  gray_04: '#333333',

  // page general styles
  page__title: colorPalette.green_75,
  page__subtitle: colorPalette.green_80,
  page__subtitle1: colorPalette.green_90,
  page__background_primary: colorPalette.beige_50,
  // buttons
  button__background_primary: colorPalette.green_100,
  button__background_secondary: colorPalette.orange_50,
  button__text_primary: colorPalette.white,

  // header
  header__background_primary: colorPalette.beige_50,
  header__label_primary: colorPalette.green_90,
  header__label_secondary: colorPalette.green_75,

  // footer
  footer_background_primary: colorPalette.beige_50,
  // card styles
  info__background_primary: colorPalette.white,
  info__label_primary: colorPalette.green_80,
  info__label_secondary: colorPalette.green_75,
  info__label_title: colorPalette.green_75,
  info__label_subtitle: colorPalette.green_75,
  info__label_tertiary: colorPalette.orange_75,
  info__label_text: colorPalette.orange_50,

  coverage__background_primary: colorPalette.green_90,
  coverage__background_secondary: colorPalette.green_75,
  coverage__label_primary: colorPalette.beige_50,
  coverage__label_secondary: colorPalette.beige_50_op_75,
  coverage__label_subtitle: colorPalette.beige_50,
  coverage__label_title: colorPalette.beige_50,
  coverage__label_paragraph: colorPalette.beige_50_op_75,

  payment__background_primary: colorPalette.green_75,
  payment__background_secondary: colorPalette.beige_25,
  terms__background_primary: colorPalette.beige_25,

  card_primary_separator: colorPalette.beige_25,

  // form styles
  form__background_primary: colorPalette.beige_25,
  form_label_primary: colorPalette.beige_50_op_70,
  // plan picker styles
  planPicker_label_primary: colorPalette.beige_50,
  planPicker_label_secondary: colorPalette.green_75,

  text_primary: colorPalette.green_90,
  text_secondary: colorPalette.green_75,
  text_disabled: colorPalette.grey_10_op_38,
  text_secondary_background: colorPalette.white,

  light_text: colorPalette.beige_50,
  medium_text: colorPalette.beige_60,
  light_background: colorPalette.white_op_10,

  card_background_primary: colorPalette.white,
  card_background_primary_editable: colorPalette.beige_25,
  card_background_secondary: colorPalette.green_90,
  card_background_tertiary: colorPalette.orange_75,
  card_background_modal: colorPalette.beige_50,

  loading_background: colorPalette.orange_40,
  loading_bar: colorPalette.orange_50,

  body: 'rgba(0, 0, 0, 0.87)',
  body_light: 'rgba(0, 0, 0, 0.70)',
  error: colorPalette.red_50,
  success: colorPalette.green_legacy_86c440
};

export const screens = {
  mobile: 500,
  tablet: 768,
  small_desktop: 1024,
  standard_desktop: 1280,
  medium_desktop: 1400,
  big_desktop: 1680
};

export const sizes = {
  headerHeight: 96,
  footerHeight: 100,
  sideColumn: 360,
  contentColumn: 744,
  roundCorners: 6
};

export const fonts = {
  // The `!important` makes sure that using a font explicitly
  // will override any font being inherited from higher up in the tree
  // (which will also be !important), but cannot be overridden by any
  // fonts coming from Material UI. (Ex: if you remove the !important,
  // tooltips from Material UI won't use the font you specify for the
  // selector '*')
  FrankRuhlLibre: `'Frank Ruhl Libre', -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif !important`,
  LibreFranklin: `'Libre Franklin', -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif !important`
};
